import { getAuthCountries } from '@services/external/auth.api';
import countries from 'i18n-iso-countries';
import React from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import ReactSelect, { FocusEventHandler } from 'react-select';
import { Select } from '../select';

type TLangs = 'sk' | 'en' | 'cs';

const ALL_LANGUAGES = Object.keys(countries.getAlpha2Codes());

const empty: string[] = [];

interface CountrySelectProps
  extends Omit<Partial<ReactSelect>, 'value' | 'beforeContent' | 'onChange'> {
  /**
   * Language in which should the countries names be displayed
   */
  language?: TLangs;
  /**
   * Country codes to display in select.
   * If not provided, all will be shown
   */
  useAuthCountries?: boolean;
  /**
   * Value of input
   */
  value?: string;
  /**
   * On change handler
   */
  onChange?: (value?: string) => void;
  variation?: 'lined' | 'round';
  onBlur?: FocusEventHandler;
}

const CountrySelect = (props: CountrySelectProps, ref?: React.Ref<ReactSelect>) => {
  const { language, value, onChange, ...rest } = props;
  const { locale } = useIntl();
  const finalLang = language ?? locale ?? 'en';

  const { data, isLoading } = useQuery(
    'enabled-countries',
    async () => {
      const countries = await getAuthCountries();
      return countries.data;
    },
    {
      enabled: props.useAuthCountries ?? false,
      initialData: [],
    },
  );

  const enabledCodes = props.useAuthCountries ? data : empty;

  const selectCountries = React.useMemo(() => {
    /**
     * register locale from package
     */
    countries.registerLocale(require(`i18n-iso-countries/langs/${finalLang}.json`));
    /**
     * map values for select
     */
    if (enabledCodes) {
      return enabledCodes?.map((code) => {
        return {
          label: countries.getName(code, finalLang),
          value: code,
        };
      });
    }
    /**
     * or return all
     */
    return ALL_LANGUAGES?.map((code) => {
      return {
        label: countries.getName(code, finalLang),
        value: code,
      };
    });
  }, [finalLang, enabledCodes]);

  /**
   * Map country code for select value
   */
  const selected = React.useMemo(
    () => selectCountries.find((county) => county.value === value),
    [selectCountries, value],
  );

  return (
    // @ts-expect-error
    <Select
      {...rest}
      options={selectCountries}
      ref={ref}
      onChange={(value: any) => (value ? onChange?.(value.value) : onChange?.(undefined))}
      value={selected}
      loading={isLoading}
    />
  );
};

export default React.forwardRef(CountrySelect);
